import { useEffect, useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useDialog } from '@/components/ui/use-dialog';
import { cn } from '@/lib/cn';

/**
 * A centred modal dialog.
 *
 * Hand-written rather than taken from Radix. When this was written it set initial
 * focus, closed on Escape and stopped there, with a note saying S5 would decide
 * whether a real trap was needed. **It was, and it is here rather than from
 * Radix** — the whole behaviour is `use-dialog.ts`, which is smaller than the
 * dependency and is needed by the correction panel too, which is not a modal and
 * so could not have used a Radix `Dialog` anyway.
 *
 * The two things a `<dialog role="dialog" aria-modal>` promises and this now
 * keeps: Tab cannot leave, and focus returns to the control that opened it.
 * Content behind is *not* hidden from assistive technology with `inert` — the
 * overlay covers it visually and the trap covers it for the keyboard, and adding
 * `inert` to a moving target (the app shell) is how you end up with a page that
 * stays inert after a crash mid-close.
 *
 * It arrives rather than appears: the backdrop fades in and the dialog rises a
 * few pixels — a sheet slides up from the bottom edge on a phone — through
 * `@starting-style`, so nothing waits on a timer and focus moves at once. It
 * leaves at once: the caller unmounts it. A dialog that replaces another in
 * the same moment (a sheet swapping itself for the banknote counter) keeps the
 * backdrop still, so the screen does not flash. Reduced motion: no movement.
 */
/** When the last dialog closed: one opening within `SWAP_MS` of it replaces it. */
let lastClosedAt = -Infinity;
const SWAP_MS = 100;

export const Modal = ({
  title,
  description,
  onClose,
  footer,
  children,
  wide,
  large,
  full,
  scrolls,
  sheet,
}: {
  title: string;
  description?: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
  wide?: boolean;
  /**
   * For a list the dialog exists to show: wider still, held to the viewport's
   * height, and the body scrolls edge to edge with no padding of its own. Its
   * top stays put, so a row that opens grows it downward, not out from the
   * centre.
   */
  large?: boolean;
  /**
   * For a table too wide and too long for any dialog: the whole screen less
   * its margin, with no padding of its own. The content is a flex column child:
   * it takes the height and scrolls itself, so it keeps its own sticky rows.
   */
  full?: boolean;
  /**
   * For a form that can outgrow the screen — a long checklist: held to the
   * viewport's height, the title and the buttons stay put and the body scrolls.
   * Not the default: a body that scrolls clips a dropdown that opens below it.
   */
  scrolls?: boolean;
  /**
   * On a phone, a sheet from the bottom edge, full width, its buttons in thumb
   * reach; from `sm` up an ordinary dialog. For a task done standing up — a
   * count, a confirmation.
   */
  sheet?: boolean;
}) => {
  const { t } = useTranslation();
  const { containerRef, initialFocusRef } = useDialog({ onClose, trap: true });
  const titleId = useId();
  const descriptionId = useId();
  const [swapped] = useState(() => performance.now() - lastClosedAt < SWAP_MS);
  useEffect(
    () => () => {
      lastClosedAt = performance.now();
    },
    [],
  );

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex justify-center overflow-y-auto bg-black/40 sm:p-4',
        sheet ? 'items-end' : 'items-start p-4',
        large || full ? 'sm:items-start' : 'sm:items-center',
        !swapped && 'motion-safe:transition-[background-color] motion-safe:duration-200 motion-safe:starting:bg-black/0',
      )}
    >
      <div
        ref={containerRef as React.RefObject<HTMLDivElement>}
        role="dialog"
        aria-modal="true"
        // Pointed at the heading rather than duplicated into aria-label: two
        // sources for the same string is one that can drift, and the heading is
        // the one a sighted user reads.
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        className={cn(
          'w-full rounded-lg border border-border-default bg-bg-raised shadow-xl',
          'motion-safe:transition-[opacity,translate] motion-safe:ease-out',
          sheet
            ? 'max-sm:motion-safe:duration-300 max-sm:motion-safe:starting:translate-y-full sm:motion-safe:duration-200 sm:motion-safe:starting:translate-y-2 sm:motion-safe:starting:opacity-0'
            : 'motion-safe:duration-200 motion-safe:starting:translate-y-2 motion-safe:starting:opacity-0',
          sheet && 'max-sm:max-h-[92dvh] max-sm:overflow-y-auto max-sm:rounded-b-none max-sm:pb-[env(safe-area-inset-bottom)]',
          (large || full || scrolls) && 'flex max-h-[calc(100dvh-2rem)] flex-col',
          full && 'h-[calc(100dvh-2rem)]',
          full ? 'max-w-none' : large ? 'max-w-5xl' : wide ? 'max-w-3xl' : 'max-w-md',
        )}
      >
        <header className="flex items-start justify-between gap-3 border-b border-border-default px-5 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-sm font-semibold text-fg">
              {title}
            </h2>
            {description ? (
              <p id={descriptionId} className="mt-0.5 text-xs text-fg-muted">
                {description}
              </p>
            ) : null}
          </div>
          <Button
            ref={initialFocusRef}
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <X aria-hidden className="size-4" />
          </Button>
        </header>

        <div
          className={cn(
            large ? 'min-h-0 flex-1 overflow-auto' : full ? 'flex min-h-0 flex-1 flex-col' : 'px-5 py-4',
            scrolls && 'min-h-0 flex-1 overflow-y-auto',
          )}
        >
          {children}
        </div>

        {footer ? (
          <footer className="flex justify-end gap-2 border-t border-border-default px-5 py-3">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  );
};
